import { Layer } from 'src/app/modules/cop/store/cop/models/layer.model';
import { Document } from 'src/app/modules/core/store/models/document.model';
import { LayerRenderer } from '../models/layer-renderer.model';
import { MapDrawingContext } from '../models/map-drawing-context.model';
import Environment from '@env';
import { AnimatedLayer } from './animated-layer';
import { LayerMapFeatures } from '../models/layer-map-features.model';

export class WeatherTileLayerRenderer implements LayerRenderer {
  private overlayCacheBustKey = new Date().getTime();

  addDocuments(drawingContext: MapDrawingContext, layer: Layer, documents: Document[]): void {

    // If the layer is currently not visible, ignore the addDocuments call and ensure the tile layer is removed
    if (!drawingContext.isLayerVisible) {
      this.removeDocuments(drawingContext, layer);
      drawingContext.onLoadingDone(layer);
      return;
    }

    // Check that Google maps is initialized
    if (!drawingContext.googleMap?.overlayMapTypes) {
      drawingContext.onLoadingDone(layer);
      return;
    }

    if (!!!drawingContext.mapFeatures[layer.uId]) {
      drawingContext.mapFeatures[layer.uId] = {};
    }
    const layerFeatures = drawingContext.mapFeatures[layer.uId];

    if (!!!layerFeatures['weather']) {
      layerFeatures['weather'] = {
        markers: [],
        polygons: [],
        polylines: [],
        circles: [],
        rectangles: [],
        imageOverlays: [],
        hash: null
      };
    }
    const docFeatures = layerFeatures['weather'];

    const isAnimated = drawingContext.mapState.layerStates[layer.uId]?.isAnimated;

    // if we already have overlays for this layer then remove
    // them from the map and redraw them
    if (docFeatures.imageOverlays.length > 0) {
      this.removeDocuments(drawingContext, layer);
    }

    const layerOpacity = this.getValidOpacity(layer.displayOptions?.shapeOpacity);
    const tileSize = layer.displayOptions?.tileSize || 256;
    // const urlTemplate = layer.foreignDatasetDefinition.urlTemplate;
    const urlTemplate = 'https://maps.aerisapi.com/{client_id}_{client_key}/{options}/{z}/{x}/{y}/current.png';
    const addBustKey = true;
    const options = (layer.foreignDatasetDefinition?.options?.filter(o => o.enabled).map(o => o.value) || []).join(',');
    const frameCount = isAnimated ? 10 : 1;
    const startMinutes = isAnimated ? -60 : 1; // start time offset relative to now, where negative means past
    const endMinutes = isAnimated ? 0 : 1;

    if (isAnimated) {
      docFeatures.animatedLayer = new AnimatedLayer();
      docFeatures.animatedLayer.name = layer.uId;
      docFeatures.animatedLayer.opacity = layerOpacity;
    }

    for (let i = 0; i < frameCount; i++) {
      const opacity = i === 0 ? layerOpacity : 0;

      const weatherOverlay = this.buildMapTileOverlay(
        layer.uId,
        opacity,
        tileSize,
        urlTemplate,
        addBustKey,
        options,
        isAnimated,
        frameCount,
        startMinutes,
        endMinutes,
        i
      );

      if (isAnimated) {
        docFeatures.animatedLayer.frames.push(weatherOverlay);
      }

      docFeatures.imageOverlays.push(weatherOverlay);

      drawingContext.googleMap.overlayMapTypes.push(weatherOverlay);
    }

    // Remove existing image overlays and re-push them in sort order
    this.reorderTileLayersDrawingOrderByZIndex(drawingContext);

    if (isAnimated) {
      docFeatures.animatedLayer.startAnimation(5000);
    }

    drawingContext.onLoadingDone(layer);
  }

  updateDocuments(drawingContext: MapDrawingContext, layer: Layer, documents?: Document[]): void {}

  removeDocuments(drawingContext: MapDrawingContext, layer: Layer, documentUIds?: string[]): void {

    if (!!!drawingContext.mapFeatures[layer.uId]) {
      drawingContext.mapFeatures[layer.uId] = {};
    }
    const layerFeatures = drawingContext.mapFeatures[layer.uId];

    if (!!!layerFeatures['weather']) {
      layerFeatures['weather'] = {
        markers: [],
        polygons: [],
        polylines: [],
        circles: [],
        rectangles: [],
        imageOverlays: [],
        hash: null
      };
    }

    this.removeDocument(drawingContext, layer, 'weather', layerFeatures);
  }

  removeDocument(drawingContext: MapDrawingContext, layer: Layer, uid: string, layerFeatures: LayerMapFeatures): void {
    // Check that Google maps is initialized
    if (!drawingContext.googleMap?.overlayMapTypes){
      return;
    }

    const docFeatures = layerFeatures[uid];

    if (!!!docFeatures) {
      return;
    }

    if (!!docFeatures.animatedLayer) {
      docFeatures.animatedLayer.stopAnimation();
      docFeatures.animatedLayer = null;
    }

    for (let weatherOverlay of docFeatures.imageOverlays) {
      const overlayIndex = drawingContext.googleMap.overlayMapTypes.getArray().indexOf(weatherOverlay);
      if (overlayIndex >= 0) {
        drawingContext.googleMap.overlayMapTypes.removeAt(overlayIndex);
      }
    }

    docFeatures.imageOverlays = [];
  }

  private buildMapTileOverlay(
    layerName: string,
    opacity: number,
    tileSize: number,
    urlTemplate: string,
    addBustKey: boolean,
    options: string,
    isAnimated: boolean = false,
    frameCount: number = 1,
    startMinutes: number = 1,
    endMinutes: number = 1,
    stepNumber: number = 1
  ): google.maps.ImageMapType {
    const that = this;
    const servers = ['1', '2', '3', '4'];

    const interval = (endMinutes - startMinutes) / frameCount;
    const timeOffset = startMinutes + interval * stepNumber;

    let currentServer = 1;

    return new google.maps.ImageMapType({
      getTileUrl: (coord, zoom) => {
        currentServer--;

        if (currentServer < 0) {
          currentServer = servers.length - 1;
        }

        if (addBustKey && urlTemplate.indexOf('?') < 1) {
          urlTemplate = urlTemplate + '?';
        } else if (addBustKey && !urlTemplate.endsWith('&') && !urlTemplate.endsWith('?')) {
          urlTemplate = urlTemplate + '&';
        }

        const aerisIds = Environment.aeris;

        return urlTemplate
            .replace('{client_id}', aerisIds.client_id)
            .replace('{client_key}', aerisIds.client_key)
            .replace('{server}', servers[currentServer])
            .replace('{options}', options)
            .replace('{z}', zoom.toString())
            .replace('{x}', coord.x.toString())
            .replace('{y}', coord.y.toString())
            .replace('current.png', !isAnimated ? 'current.png' : `${timeOffset}min.png`) +
          (addBustKey ? that.overlayCacheBustKey.toString() : '');
      },
      tileSize: new google.maps.Size(tileSize, tileSize),
      opacity,
      name: layerName
    });
  }

  private reorderTileLayersDrawingOrderByZIndex(drawingContext: MapDrawingContext){
    // Remove existing image overlays and re-push them in sort order
    var tileOverlays = [...drawingContext.googleMap.overlayMapTypes.getArray()];
    drawingContext.googleMap.overlayMapTypes.clear();

    var tileLayerUIds = tileOverlays.map(o => o.name);
    var tileLayers = drawingContext.mapState.layers.filter(l => tileLayerUIds.includes(l.uId));
    tileLayers.sort((a, b) => (a.displayOptions.tileLayerZIndex ?? 0) - (b.displayOptions.tileLayerZIndex ?? 0))

    tileLayers.forEach(l => {
      tileOverlays
        .filter(o => o.name == l.uId)
        .map(o => drawingContext.googleMap.overlayMapTypes.push(o));
    })
  }

  private getValidOpacity(opacity: number): number {
    // Force opacity into a valid range
    if (!opacity && opacity !== 0) {
      opacity = 1;
    }

    if (opacity > 1) {
      opacity = 1;
    } else if (opacity < 0) {
      opacity = 0;
    }

    return opacity;
  }
}
